import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../apiConfig/api';

const WhatsNew = ({ isTamil = false }) => {
  const [circulars, setCirculars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const translations = {
    whatsNew: {
      en: "What's New",
      ta: "புதியவை"
    },
    latestUpdates: {
      en: "Latest Updates",
      ta: "புதிய அறிவிப்புகள்"
    },
    noUpdates: {
      en: "No updates available",
      ta: "புதுப்பிப்புகள் எதுவும் இல்லை"
    }
  };

  useEffect(() => {
    const fetchCirculars = async () => {
      try {
        setLoading(true);
        const response = await api.get('/listWhatsNew');
        const transformedData = response.data.results.map(item => ({
          id: item.id,
          title: {
            en: item.nameEnglish,
            ta: item.nameTamil
          },
          date: item.dateOfUpload,  // Changed from createdAt to dateOfUpload
          link: `/circulars/${item.id}`,
          pdfName: item.fileEnglish || item.fileTamil || '',
          fileEnglish: item.fileEnglish,
          fileTamil: item.fileTamil
        }));
        setCirculars(transformedData);
      } catch (err) {
        setError('Failed to fetch circulars');
        console.error('Error fetching circulars:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCirculars();
  }, []);

  // Function to render a circular item
  const renderCircularItem = (circular, index) => (
    <div
      key={`${circular.id}-${index}`}
      className="group inline-flex items-center gap-6 bg-[#2C251D] bg-opacity-5 hover:bg-opacity-10
                 px-8 py-5 rounded-xl border-2 border-[#2C251D] border-opacity-10
                 hover:border-opacity-20 transition-all duration-300"
    >
      {/* Date Badge */}
      <div className="flex flex-col items-center justify-center bg-[#2C251D] rounded-lg p-3
                      min-w-[64px] text-white">
        <span className="font-bold text-xl">
          {new Date(circular.date).getDate()}
        </span>
        <span className="font-medium text-sm">
          {new Date(circular.date).toLocaleString('default', { month: 'short' })}
        </span>
      </div>

      {/* Circular Title and Actions */}
      <div className="flex-1">
        <Link to={circular.link}>
          <h3 className="text-[#2C251D] font-bold text-lg group-hover:text-[#000000]
                         transition-colors duration-300">
            {circular.title[isTamil ? 'ta' : 'en']}
          </h3>
        </Link>
      </div>

      {/* Download and Arrow Icons */}
      <div className="flex items-center gap-4">
        {(isTamil ? circular.fileTamil : circular.fileEnglish) && (
          <a
            href={`${api.defaults.baseURL.split('/api')[0]}/${isTamil ? circular.fileTamil : circular.fileEnglish}`}
            download
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 text-[#2C251D]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </a>
        )}
        <motion.div
          initial={{ x: 0 }}
          whileHover={{ x: 5 }}
          className="text-[#2C251D]"
        >
          <ArrowRight className="w-6 h-6" />
        </motion.div>
      </div>
    </div>
  );
  // Empty state placeholder
  const renderEmptyState = () => (
    <div className="group inline-flex items-center gap-6 bg-[#2C251D] bg-opacity-5
                    px-8 py-5 rounded-xl border-2 border-[#2C251D] border-opacity-10
                    min-w-[400px]">
      <div className="flex-1 text-center text-[#2C251D] font-medium">
        {translations.noUpdates[isTamil ? 'ta' : 'en']}
      </div>
    </div>
  );

  // Always render the section, but show different content based on state
  return (
    <section className="py-24 bg-gradient-to-b from-white to-[#F5F1EA]">
      <div className="container mx-auto px-4">
        {/* Section Header - Always visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-[#726354] font-semibold tracking-wider uppercase text-sm mb-4 block">
            {translations.latestUpdates[isTamil ? 'ta' : 'en']}
          </span>
          <h2 className={`text-[#2C251D] font-bold ${
            isTamil 
              ? 'text-3xl md:text-4xl'
              : 'text-4xl md:text-5xl'
          } mb-6`}>
            {translations.whatsNew[isTamil ? 'ta' : 'en']}
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#8A7968] to-[#726354] mx-auto rounded-full" />
        </motion.div>

        {/* Content Area - Always visible */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative max-w-7xl mx-auto"
        >
          <div className="relative overflow-hidden bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center gap-6">
              {/* Animated Icon - Always visible */}
              <div className="flex-shrink-0">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 bg-[#2C251D] rounded-full flex items-center justify-center shadow-lg"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </motion.div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-pulse">Loading...</div>
                  </div>
                ) : error ? (
                  <div className="text-red-600 text-center">{error}</div>
                ) : circulars.length === 0 ? (
                  // Empty state with marquee animation
                  <motion.div
                    animate={{
                      x: [-400, 0]
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      repeatType: "loop",
                      ease: "linear"
                    }}
                    className="whitespace-nowrap flex items-center gap-8"
                  >
                    {[1, 2].map((_, index) => (
                      <React.Fragment key={index}>
                        {renderEmptyState()}
                      </React.Fragment>
                    ))}
                  </motion.div>
                ) : circulars.length === 1 ? (
                  <div className="flex items-center">
                    {renderCircularItem(circulars[0], 0)}
                  </div>
                ) : (
                  <motion.div
                    animate={{
                      x: [0, -((circulars.length) * 400)]
                    }}
                    transition={{
                      x: {
                        duration: circulars.length * 10,
                        repeat: Infinity,
                        repeatType: "loop",
                        ease: "linear"
                      }
                    }}
                    className="whitespace-nowrap flex items-center gap-8"
                  >
                    {[...circulars, ...circulars].map((circular, index) => 
                      renderCircularItem(circular, index)
                    )}
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatsNew;